import { useState } from 'react';
import { App, Button, Flex, Input, Space, Typography } from 'antd';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { LinesGrid } from '@/shared/ui/LinesGrid';
import type { LinesGridColumn } from '@/shared/ui/LinesGrid';
import { useCreateCategoryMutation } from '@/entities/category';
import { translateApiError } from '@/shared/api/errorToMessage';

export interface RootGroupCreateWindowProps {
  onClose: () => void;
}

interface Row {
  key: string;
  name: string;
}

let keySeq = 0;
function emptyRow(): Row {
  keySeq += 1;
  return { key: `grp${keySeq}-${Date.now()}`, name: '' };
}

/** §2.1: добавление корневых групп категорий — та же табличная форма, что у
 *  `CategoryCreateWindow`/`CounterpartyCreateWindow`, вместо одиночной формы `CategoryWindow`:
 *  несколько групп за один заход, строка снизу добавляется автоматически по заполнению
 *  «Название», пустые строки не сохраняются. Группа не имеет `pcsPerBox` (DB CHECK
 *  `(parent_id IS NULL)=(pcs_per_box IS NULL)`), поэтому колонка N здесь не нужна. */
export function RootGroupCreateWindow({ onClose }: RootGroupCreateWindowProps) {
  const { message } = App.useApp();
  const [rows, setRows] = useState<Row[]>([emptyRow()]);
  const [createCategory, { isLoading }] = useCreateCategoryMutation();

  function addRow() {
    setRows((prev) => [...prev, emptyRow()]);
  }

  function removeRow(key: string) {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  }

  function updateName(key: string, rowIndex: number, value: string) {
    setRows((prev) => {
      const wasEmpty = !prev[rowIndex]?.name;
      const next = prev.map((r) => (r.key === key ? { ...r, name: value } : r));
      if (rowIndex === prev.length - 1 && wasEmpty && value.trim()) {
        return [...next, emptyRow()];
      }
      return next;
    });
  }

  const filled = rows.filter((r) => r.name.trim());

  async function handleSave() {
    if (filled.length === 0) return;
    try {
      await Promise.all(filled.map((r) => createCategory({ name: r.name.trim() }).unwrap()));
      message.success(filled.length > 1 ? `Добавлено групп: ${filled.length}` : 'Группа добавлена');
      onClose();
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  const columns: LinesGridColumn<Row>[] = [
    {
      key: 'name',
      title: 'Название',
      width: 260,
      render: (row, rowIndex) => (
        <Input
          className="nav-cell"
          data-nav-row={rowIndex}
          data-nav-col={0}
          value={row.name}
          autoFocus={rowIndex === 0}
          onChange={(e) => updateName(row.key, rowIndex, e.target.value)}
        />
      ),
    },
  ];

  return (
    <FloatingWindow
      kind="rootGroupCreate"
      title="Новые группы"
      onClose={onClose}
      footer={
        <Space>
          <Button type="primary" loading={isLoading} disabled={filled.length === 0} onClick={() => void handleSave()}>
            Сохранить{filled.length > 1 ? ` (${filled.length})` : ''}
          </Button>
          <Button onClick={onClose}>Закрыть</Button>
        </Space>
      }
    >
      <Flex vertical gap={8}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Заполните название — строка снизу добавится автоматически. Пустые строки не сохраняются.
        </Typography.Text>
        <LinesGrid lines={rows} columns={columns} getRowKey={(r) => r.key} onAddRow={addRow} onRemoveRow={removeRow} />
      </Flex>
    </FloatingWindow>
  );
}
